import { AccountApi, CoreApi } from 'service/axios-api';
import { rootStore } from 'store/root';
import history from './history';
import plex from './plex';
import { sleep } from './utils';
import { stopNexonlink } from './nexonlink';

const { application, coreappIpc, sessionStorage, win } = window;

export const tryAutoLogin = async () => {
    const { session } = rootStore;

    if (!session.autoLogin) {
        return false;
    }

    try {
        const data: any = await AccountApi.post('/login/launcher/autologin', {
            deviceId: application.params.device_id,
        });
        window.dataLayer.push({ event: 'loginSuccess', method: 'launcher.autologin' });

        data.autoLogin = true;
        initSession(data, true);
        return true;
    } catch (e) {
        return false;
    }
};
export const initSession = async (
    {
        autoLogin,
        userNo,
        globalUserNo,
        loginSessionExpiresIn,
        hashedUserNo,
        hashedGlobalUserNo,
        productId = '10000',
    }: {
        autoLogin: boolean; // auto login checkbox carry over from account web
        userNo: number;
        globalUserNo: number;
        loginSessionExpiresIn: number;
        hashedUserNo: string;
        hashedGlobalUserNo: string;
        productId: string;
    },
    isAutologin: boolean = false // launcher is launched with auto login session
) => {
    application.userNo = userNo.toString();
    if (globalUserNo) {
        application.globalUserNo = globalUserNo.toString();
    }

    sessionStorage.setItem('hashedUserNo', hashedUserNo);
    sessionStorage.setItem('hashedGlobalUserNo', hashedGlobalUserNo);

    coreappIpc.send('sendCCUHeartbeatLoop', userNo);

    plex.sendStats('login', 'success', '', { is_auto: isAutologin });
    plex.sendPartner('login', productId, userNo);

    rootStore.session.set({ autoLogin });
    sessionStorage.setItem('refreshSessionAt', Date.now() + loginSessionExpiresIn * 1000);

    window.win.show();

    win.webContents.clearHistory();

    if (rootStore.regionConfig.cmsWebCode === 'tw') {
        await rootStore.appStore.nexonlink.start();
    }

    if (isAutologin) history.replace('/main');
    else window.location.assign(window.location.href.replace('/login', `/main?_ts=${Date.now()}`)); // if it's normal login, refresh main page for up-to-date FE patch
};

export const logout = async () => {
    const params = {
        deviceId: application.params.device_id,
    };
    localStorage.setItem('refreshSessionAt', '0');

    try {
        await AccountApi.post('/logout/launcher', params);
    } finally {
        /* continue regardless of error */
    }

    if (rootStore.regionConfig.cmsWebCode === 'tw') {
        stopNexonlink();
        rootStore.appStore.nexonlink.clearStreamingInfo();
    }
    coreappIpc.send('restart');
};

export const refreshTokenLoop = async () => {
    const bufferTime = (rootStore.session.autoLogin ? 24 : 0.5) * 60 * 60 * 1000; // 24 hours (autologin), 30 min - buffer time before it's expired (ms)
    for (;;) {
        const refreshAt: number = parseInt(sessionStorage.getItem('refreshSessionAt') || '0', 10);
        const currentTime = Date.now();
        if (currentTime > refreshAt - bufferTime) {
            const result = await refreshToken();
            if (!result) {
                if (currentTime > refreshAt) expireSession('failed to refresh token');
                await sleep(5 * 60 * 1000 * Math.random());
            }
        }
        await sleep(5 * 60 * 1000);
    }
};

export const refreshToken = async () => {
    try {
        const { loginSessionExpiresIn }: { loginSessionExpiresIn: number } = await AccountApi.put('/login/launcher/refresh', {
            deviceId: application.params.device_id,
        });
        sessionStorage.setItem('refreshSessionAt', Date.now() + loginSessionExpiresIn * 1000); // set expiration time as half time
        return true;
    } catch (e) {
        // plex.sendError('refreshToken failed', e);
        // expireSession('failed to refresh token');
        // throw e;
        return false;
    }
};

export const expireSession = (reason = '') => {
    if (window.location.host.indexOf('localhost') >= 0) {
        console.error('session_expired', reason);
        console.info('restarting has been canceled due to local test');
        return;
    }
    plex.sendStats('launcher_restart', 'session_expired', '10000', { reason });
    coreappIpc.send('restart');
};

// this game ticket will be passed to game client via (coreApp -> sdk) after game launch
export const callApiWithIdToken = async (url: string, params: any = {}) => {
    console.log('callApiWithIdToken request ', url, params);
    const optional = {} as any;

    const branch = rootStore.appStore.library.find((x) => x.productId === params.product_id)?.currentBranch;
    if (branch) {
        const branchName = branch.branchName;
        const toyServiceId = branch.toyServiceId;
        if (branchName && branchName !== 'public') optional.branchName = branchName;
        if (toyServiceId) optional.toyServiceId = toyServiceId;
    }
    const { data: result } = await CoreApi.post('/game-auth2/v1/ticket', { productId: params.product_id, ...optional });
    return result;
};

// for coreApp backword compatibility
coreappIpc.on('callApiWithIdToken', callApiWithIdToken);
